/**
 * Flatlogic Dashboards (https://flatlogic.com/admin-dashboards)
 *
 * Copyright © 2015-present Flatlogic, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from "react";
import cx from "classnames";
import { Switch, Route, withRouter, Redirect } from "react-router";
import { connect } from 'react-redux';

import s from "./Layout.module.scss";
import Header from "../Header";
import Footer from "../Footer";
import Sidebar from "../Sidebar";
import ReactHtmlParser from 'react-html-parser';

// Dashboard component is loaded directly as an example of server side rendering
import Dashboard from "../../pages/dashboard";
import DashboardMain from "../../pages/dashboard/DashboardMain";
import NotFound from "../../pages/notFound";
import Bill from "../../pages/bill";
import BillNew from "../../pages/bill/BillNew";
import Profile from "../../pages/profile";
import Privacy from "../../pages/privacy";
import TermsOfUse from "../../pages/termsOfUse";
import User from "../../pages/user";
import Review from "../../pages/review";
import UpdateOrder from "../../pages/updateOrder";
import NewUpdateOrder from "../../pages/updateOrder/NewUpdateOrder";
import Packet from "../../pages/packet/Packet";
import PacketDetail from "../../pages/packet/PacketDetail";
import Barcode from "../../pages/barcode/Barcode";
import Import from "../../pages/barcode/Import";
import Export from "../../pages/barcode/Export";
import Return from "../../pages/barcode/Return";
import Tracking from "../../pages/tracking/Tracking";
import Unauthorized from '../../pages/unauthorized';
import grantPermission from '../../utils/grantPermission'
import { USER_ROLE } from '../../constants';
import { withCookies } from "react-cookie";
import Pickup from "../../pages/pickup";
import PickupDetail from "../../pages/pickup/PickupDetail";
import PackingDetail from "../../pages/pickup/PackingDetail";
import Billup from "../../pages/pickup/Billup";
import Packing from "../../pages/pickup/Packing";
import Cash from "../../pages/cash/Cash";
import CashDetail from "../../pages/cash/CashDetail";
import Pricing from "../../pages/dashboard/Pricing";
import Customer from "../../pages/pickup/customer";
import CustomerReceiver from "../../pages/pickup/CustomerReceiver";
import Mawb from "../../pages/tracking/mawb";
import MawbDetails from "../../pages/tracking/mawbDetails";
import MawbPackage from "../../pages/tracking/mawbPackage";
import MawbPackageDetail from "../../pages/tracking/mawbPackageDetails";
import ManagePricing from "../../pages/pricing/ManagePricing";
import ManagePricingDetail from "../../pages/pricing/ManagePricingDetail";
import ManagePricingOpsDetail from "../../pages/pricing/ManagePricingOpsDetail";
import EditManagePricingDetail from "../../pages/pricing/EditManagePricingDetail";

import MawbExport from "../../pages/mawb/mawb";
import MawbExportDetail from "../../pages/mawb/mawb-detail";

import ManageService from "../../pages/pricing/ManageService";
import ManageExtraCharge from "../../pages/pricing/ManageExtraCharge";


import {
  Modal, ModalHeader, ModalBody,
} from 'reactstrap';
import DashboardDetail from "../../pages/dashboard/DashboardDetail";
import PricingDetail from "../../pages/dashboard/PricingDetail";
import GroupDetail from "../../pages/dashboard/GroupDetail";
import ReportDetail from "../../pages/report/Detail";
import Report from "../../pages/report";
import ReportNew from "../../pages/report/report-new.js";
import CSKHReport from "../../pages/report/cskh-report";
import Debit from "../../pages/report/debit";
import Wiseway from "../../pages/wiseway";
import LabelUs from "../../pages/label-us";
import USPS from "../../pages/label-us/usps";
import USPSImport from "../../pages/label-us/import";
import jwt from "jsonwebtoken";
import ForcePasswordChangeModal from "../ForcePasswordChangeModal/ForcePasswordChangeModal";
import axios from 'axios';

const CustomRouter = ({ component: Component,path, roles, ...rest }) => {
  const grantAccess = grantPermission(roles, path)

  return (
    <>
      {grantAccess && (<Route {...rest} render={(props) => ( <> <Component {...props} /> </> )} />)}
      {!grantAccess && (<Redirect to="/app/unauthorized"/>)}
    </>
  )
}

class Layout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sidebarOpen: false,
      isShowModal: [],
      highlight: {},
      showPasswordChangeModal: false,
      passwordChanged: true
    };
    this.closeModal = this.closeModal.bind(this);
  }

  async componentDidMount() {
    try {
      // Check password change status
      const passwordChanged = localStorage.getItem('passwordChanged');
      if (passwordChanged === 'false' || passwordChanged === null) {
        try {
          const response = await axios.get('/check-password-change', { withCredentials: true });
          const needsChange = !response.data.passwordChanged;
          this.setState({ 
            showPasswordChangeModal: needsChange,
            passwordChanged: response.data.passwordChanged 
          });
          if (needsChange) {
            localStorage.setItem('passwordChanged', 'false');
          }
        } catch (e) {
          console.error('Error checking password status:', e);
        }
      } else {
        this.setState({ passwordChanged: passwordChanged === 'true' });
      }

      const config = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: '{notificationsHighlight{title,body,highlight,id,role}}',
        }),
        credentials: 'include',
      };
      const response = await fetch('/graphql', config)
      let { data: {notificationsHighlight: highlight} } = await response.json();
      // filter by role
      let token = localStorage.getItem('id_token');
      let currUser = jwt.decode(token);
      if (currUser && currUser.role !== USER_ROLE.ADMIN) {
        highlight = highlight.filter(item => item.role === currUser.role || !item.role)

        if (currUser.role === 'ops') {
          highlight = highlight.filter(item => item.role === 'ops')
        }
      }

      this.setState({ highlight })
      if (highlight.length === 0) return;
      const ids = highlight.map(value => Number(value.id));
      const currentIds = JSON.parse(localStorage.getItem('openModalNotifyIds') || '[]');
      const isEqual = (JSON.stringify(ids.sort()) === JSON.stringify(currentIds.sort()));
      let isShow = localStorage.getItem('openModalNotify') || !isEqual
      localStorage.setItem('openModalNotifyIds', JSON.stringify(ids));
      isShow = Array(highlight.length).fill(isShow)
      await this.setState({isShowModal: isShow})
    } catch (e) {
      console.log(e)
    }
  }

  async closeModal (i) {
    const isShowModal = [...this.state.isShowModal]
    isShowModal[i] = false
    await this.setState({isShowModal})
    localStorage.removeItem('openModalNotify')
  }

  render() {
    const { highlight } = this.state
    return (
      <div className={s.root}>
        <Sidebar />
        <div
          className={cx(s.wrap, { [s.sidebarOpen]: this.state.sidebarOpen })}
        >
          <div className={s.header}>
            <Header
              sidebarToggle={() =>
                this.setState({
                  sidebarOpen: !this.state.sidebarOpen,
                })
              }
            />
          </div>

          <main className={s.content}>
            <Switch>
              <CustomRouter path="/app/dashboard" exact component={DashboardMain} roles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.CONSULTANT, USER_ROLE.DOCUMENT]} />
              <CustomRouter path="/app/main" exact component={Dashboard} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.DOCUMENT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.OPS, USER_ROLE.MANAGER, USER_ROLE.ACCOUNTANT, USER_ROLE.SHIPPER]} />
              <CustomRouter path="/app/bill" exact component={BillNew} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.DOCUMENT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.OPS, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER]} />
              <CustomRouter path="/app/bill-new" exact component={BillNew} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.DOCUMENT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.OPS, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER]} />
              <CustomRouter path="/app/packet" exact component={Packet} />
              <CustomRouter path="/app/packet-detail/:id" exact component={PacketDetail} />
              <CustomRouter path="/app/tracking" exact component={Tracking} />
              <CustomRouter path="/app/barcode" exact component={Barcode} />
              <CustomRouter path="/app/barcode/import" exact component={Import} />
              <CustomRouter path="/app/barcode/export" exact component={Export} />
              <CustomRouter path="/app/barcode/return" exact component={Return} />
              <CustomRouter path="/app/privacy" exact component={Privacy} />
              <CustomRouter path="/app/tou" exact component={TermsOfUse} />
              <CustomRouter path="/app/profile" exact component={Profile} />
              <CustomRouter path="/app/users/:userId?" exact component={Profile} roles={[USER_ROLE.ADMIN]} />
              <CustomRouter path="/app/user" exact component={User} roles={[USER_ROLE.ADMIN]} />
              <CustomRouter path="/app/waiting-approve" exact component={Review} roles={[USER_ROLE.ADMIN]} />
              <CustomRouter path="/app/bill/:billId" exact component={NewUpdateOrder} />
              <CustomRouter path="/app/bill-new/:billId" exact component={NewUpdateOrder} />
              <CustomRouter path="/app/main/:dashboardId" exact component={DashboardDetail} />
              <CustomRouter path="/app/tracking/management" exact component={Mawb} roles={[USER_ROLE.ADMIN, USER_ROLE.DOCUMENT, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER]}/>
              <CustomRouter path="/app/tracking/management/:code" exact component={MawbDetails}  roles={[USER_ROLE.ADMIN,USER_ROLE.DOCUMENT, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER]
              }/>
              <CustomRouter path="/app/tracking/management/:code/packages" exact component={MawbPackage}  roles={[USER_ROLE.ADMIN,USER_ROLE.DOCUMENT, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER]} />
              <CustomRouter path="/app/tracking/:kgCode/management" exact component={MawbPackageDetail} roles={[USER_ROLE.ADMIN,USER_ROLE.DOCUMENT, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER]} />
              <CustomRouter path="/app/pricing/:pricingId" exact component={PricingDetail} />
              <CustomRouter path="/app/pricing/group/:groupId" exact component={GroupDetail} />
              <CustomRouter path="/app/report" exact component={Report} oles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER, USER_ROLE.SALER]}/>
              <CustomRouter path="/app/report-new" exact component={ReportNew} roles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.MANAGER, USER_ROLE.SALER, USER_ROLE.CONSULTANT]}/>
              <CustomRouter path="/app/cskh-report" exact component={CSKHReport} roles={[USER_ROLE.ADMIN, USER_ROLE.DOCUMENT, USER_ROLE.ACCOUNTANT, USER_ROLE.CUSTOMER, USER_ROLE.MANAGER]}/>
              <CustomRouter path="/app/report/debit" exact component={Debit} />
              <CustomRouter path="/app/wiseway" exact component={Wiseway} />
              <CustomRouter path="/app/label-us" exact component={LabelUs} />
              <CustomRouter path="/app/usps" exact component={USPS} />
              <CustomRouter path="/app/usps/import" exact component={USPSImport} />
              <CustomRouter path="/app/report/:name" exact component={ReportDetail} />
              <CustomRouter path="/app/pickup" exact component={Pickup} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER,USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/pickup/customer" exact component={Customer} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS]}/>
              <CustomRouter path="/app/customer-receiver/:customerId" exact component={CustomerReceiver} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS]}/>
              <CustomRouter path="/app/pickup-detail/:pickupId" exact component={PickupDetail} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/packing-detail/:packingId" exact component={PackingDetail} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/billup" exact component={Billup} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.DOCUMENT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS]}/>
              <CustomRouter path="/app/packing" exact component={Packing} roles={[USER_ROLE.ADMIN, USER_ROLE.CUSTOMER, USER_ROLE.ACCOUNTANT, USER_ROLE.DOCUMENT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/thu-tien-mat" exact component={Cash} roles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/cash-detail/:cashId" exact component={CashDetail} roles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER, USER_ROLE.OPS, USER_ROLE.SHIPPER]}/>
              <CustomRouter path="/app/pricing" exact component={Pricing} roles={[USER_ROLE.ADMIN, USER_ROLE.ACCOUNTANT, USER_ROLE.SALER, USER_ROLE.CONSULTANT, USER_ROLE.MANAGER]}/>
              <Route path="/app/unauthorized" component={Unauthorized}/>



              <CustomRouter path="/app/manage-pricing" exact component={ManagePricing} roles={[USER_ROLE.ADMIN]} />
              <CustomRouter path="/app/manage-pricing-detail/:moneyPricingId" exact component={ManagePricingDetail}  roles={[USER_ROLE.ADMIN]}/>
              <CustomRouter path="/app/manage-pricing-ops-detail/:moneyPricingId" exact component={ManagePricingOpsDetail}  roles={[USER_ROLE.ADMIN]}/>
              <CustomRouter path="/app/edit-manage-pricing-detail/:moneyPricingId/:serviceName/:priceType" exact component={EditManagePricingDetail}  roles={[USER_ROLE.ADMIN]}/>

              <CustomRouter path="/app/manage-service" exact component={ManageService} roles={[USER_ROLE.ADMIN]} />
              <CustomRouter path="/app/manage-extracharge" exact component={ManageExtraCharge} roles={[USER_ROLE.ADMIN]} />

              <CustomRouter path="/app/mawb-export" exact component={MawbExport} roles={[USER_ROLE.ADMIN, USER_ROLE.DOCUMENT]} />
              <CustomRouter path="/app/mawb-export/:mawbCode" exact component={MawbExportDetail} roles={[USER_ROLE.ADMIN, USER_ROLE.DOCUMENT]} />

              <Route component={NotFound} />
            </Switch>
          </main>
          <Footer />
        </div>
        {highlight.length > 0 && highlight.map((note, i) =>
        <Modal isOpen={this.state.isShowModal[i]} backdrop="static" size="xl">
          <ModalHeader className="text-danger modal-header" toggle={e => this.closeModal(i)}> {note.title} </ModalHeader>
          <ModalBody>
            { ReactHtmlParser (note.body) }
          </ModalBody>
        </Modal>
      )}
        <ForcePasswordChangeModal 
          isOpen={this.state.showPasswordChangeModal}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    dashboard: state.dashboard.dashboard,
  };
}

export default withCookies(withRouter(connect(mapStateToProps)(Layout)));

